import { Schema } from "effect";
import type { StationRole, AuthenticatedStationPrincipal, StationCapability, AuthorizationDecision, GateCondition, RecordedSafetyApproval, RoleGrant } from "./model.ts";

const roleRank: Readonly<Record<StationRole, number>> = Object.freeze({
  reader: 0,
  runner: 1,
  reviewer: 2,
  maintainer: 3,
  owner: 4
});

function hasRole(
  principal: AuthenticatedStationPrincipal,
  minimum: StationRole
): boolean {
  return roleRank[principal.role] >= roleRank[minimum];
}

const allow = (): AuthorizationDecision => ({ allowed: true, reason: "allowed" });
const deny = (reason: AuthorizationDecision["reason"]): AuthorizationDecision => ({
  allowed: false,
  reason
});

/** Server code constructs scoped capability inputs from authoritative state. */
export function decideStationCapability(
  principal: AuthenticatedStationPrincipal | null,
  stationId: string,
  capability: StationCapability
): AuthorizationDecision {
  if (!principal) return deny("authentication-required");
  if (principal.stationId !== stationId) return deny("station-mismatch");

  switch (capability.kind) {
    case "source.read":
      return hasRole(principal, "reader") ? allow() : deny("insufficient-role");
    case "development.manage":
      return hasRole(principal, "maintainer")
        ? allow()
        : deny("insufficient-role");
    case "worktree.mutate":
      if (capability.assignedPrincipalId !== principal.principalId) {
        return deny("scope-mismatch");
      }
      return hasRole(principal, "runner") ? allow() : deny("insufficient-role");
    case "lease.reassign":
      return hasRole(principal, "maintainer")
        ? allow()
        : deny("insufficient-role");
    case "run.cancel":
      if (hasRole(principal, "maintainer")) return allow();
      if (capability.assignedPrincipalId !== principal.principalId) {
        return deny("scope-mismatch");
      }
      return hasRole(principal, "runner") ? allow() : deny("insufficient-role");
    case "review.disposition": {
      if (capability.disposition === "address") {
        const ownsFindingWork =
          capability.authorPrincipalId === principal.principalId ||
          capability.assignedRunnerPrincipalId === principal.principalId;
        return ownsFindingWork || hasRole(principal, "maintainer")
          ? allow()
          : deny("scope-mismatch");
      }
      if (capability.disposition === "reopen") {
        return hasRole(principal, "reviewer")
          ? allow()
          : deny("insufficient-role");
      }
      if (
        capability.blocking &&
        (capability.severity === "critical" ||
          capability.category.toLowerCase() === "security")
      ) {
        return deny("requires-two-principal-approval");
      }
      return hasRole(
        principal,
        capability.blocking ? "maintainer" : "reviewer"
      )
        ? allow()
        : deny("insufficient-role");
    }
    case "safety.override":
      return deny("requires-two-principal-approval");
    case "target.publish":
    case "roles.manage":
      return hasRole(principal, "owner") ? allow() : deny("insufficient-role");
    default:
      return deny("unknown-capability");
  }
}

const nonOverridableConditions = Schema.is(Schema.Literals([
  "source-conflict",
  "stale-target",
  "isolation-failure",
  "invalid-authorization",
  "publication-cas-failure"
]));

/** Recheck every approval against current grants at the moment of use. */
export function decideSafetyAction(
  stationId: string,
  condition: GateCondition,
  approvals: readonly RecordedSafetyApproval[],
  currentGrants: readonly RoleGrant[]
): AuthorizationDecision {
  if (nonOverridableConditions(condition)) return deny("non-overridable");
  const grantByPrincipal = new Map(
    currentGrants
      .filter((grant) => grant.stationId === stationId && grant.revokedAt === undefined)
      .map((grant) => [grant.principalId, grant])
  );
  const approved = new Map<string, RoleGrant>();
  for (const approval of approvals) {
    const grant = grantByPrincipal.get(approval.principalId);
    if (grant && grant.revision === approval.grantRevision) {
      approved.set(approval.principalId, grant);
    }
  }
  if (approved.size < 2) return deny("requires-two-principal-approval");
  if (![...approved.values()].some((grant) => grant.role === "owner")) {
    return deny("requires-two-principal-approval");
  }
  return allow();
}
